import React, { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { listPurchases, purchaseAnalytics } from './api';
import { formatDate } from './client/dates';
import KpiRow from './sales/KpiRow';
import RevenueTrend from './sales/RevenueTrend';
import TopClasses from './sales/TopClasses';
import ExpiringSoon from './sales/ExpiringSoon';
import { SourceMix, StateMix } from './sales/MixBars';
import { money, moneyExact, pctChange } from './sales/theme';

const STATUSES = ['pending', 'paid', 'canceled', 'refunded', 'expired'];
const PER_PAGE = 50;

const isoDaysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
const today = () => new Date().toISOString().slice(0, 10);

// Deltas need a bounded window to compare against, so the default view is a period
// rather than all-time. Every figure is labeled with the period it covers.
const PERIODS = [
  { key: '30d', label: '30 days', from: () => isoDaysAgo(29), to: today },
  { key: '90d', label: '90 days', from: () => isoDaysAgo(89), to: today },
  { key: '12m', label: '12 months', from: () => isoDaysAgo(364), to: today },
  { key: 'all', label: 'All time', from: () => '', to: () => '' },
];
const DEFAULT_PERIOD = '90d';
const periodDates = (key) => {
  const p = PERIODS.find((x) => x.key === key);
  return p ? { from: p.from(), to: p.to() } : { from: '', to: '' };
};

const control = 'text-xs px-3 py-1.5 rounded-full border border-gray-300 dark:border-gray-600 ' +
  'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 dark:[&>option]:bg-gray-800';

export default function SalesDashboard() {
  // The purchase list lives behind its own sub-tab. Stacked under the analytics it
  // sat a full screen down, so every filter change meant scrolling past charts to
  // see whether the rows had actually changed.
  const [view, setView] = useState('overview');
  const [q, setQ] = useState('');
  const [period, setPeriod] = useState(DEFAULT_PERIOD);
  const [filters, setFilters] = useState({ status: '', source: '', q: '', ...periodDates(DEFAULT_PERIOD) });
  const [page, setPage] = useState(1);

  // Typing must not fire a request per keystroke. Only the text box waits; selects,
  // dates and the period buttons commit immediately.
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) => (f.q === q ? f : { ...f, q }));
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const query = { ...filters, page, per_page: PER_PAGE };
  const purchasesQ = useQuery({
    queryKey: ['training-purchases', query],
    queryFn: () => listPurchases(query),
    placeholderData: keepPreviousData, // hold the current page while the next loads
  });
  // Analytics ignores paging: the overview summarizes the whole filtered set.
  const analyticsQ = useQuery({
    queryKey: ['training-purchase-analytics', filters],
    queryFn: () => purchaseAnalytics(filters),
    placeholderData: keepPreviousData,
  });

  const narrowed = Boolean(filters.status || filters.source || filters.q);
  // Merge functionally, never from the captured `filters`. The search box commits
  // on a 300ms timer, so a click landing inside that window would otherwise spread
  // a stale snapshot over the debounced q and silently drop what was just typed.
  const set = (key) => (e) => {
    const { value } = e.target;
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
  };
  const pickPeriod = (key) => {
    setPeriod(key);
    setFilters((f) => ({ ...f, ...periodDates(key) }));
    setPage(1);
  };
  const setDate = (key) => (e) => {
    const { value } = e.target;
    setPeriod('custom');
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
  };
  const clear = () => {
    setQ('');
    setPeriod(DEFAULT_PERIOD);
    setFilters({ status: '', source: '', q: '', ...periodDates(DEFAULT_PERIOD) });
    setPage(1);
  };

  const purchases = purchasesQ.data;
  const analytics = analyticsQ.data;
  const rows = purchases?.purchases || [];
  const total = purchases?.total ?? 0;
  const totalPages = purchases?.total_pages ?? 1;
  const firstOnPage = total === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const periodLabel = PERIODS.find((p) => p.key === period)?.label;
  const revenueDelta = pctChange(analytics?.revenue_cents ?? 0, analytics?.prior?.revenue_cents);

  const controls = (
    <div className="flex flex-wrap gap-2 mb-4 items-center">
      <div className="flex rounded-full border border-gray-300 dark:border-gray-600 overflow-hidden" role="group" aria-label="Period">
        {PERIODS.map((p) => (
          <button key={p.key} type="button" onClick={() => pickPeriod(p.key)} aria-pressed={period === p.key}
                  className={`text-xs px-3 py-1.5 ${period === p.key
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
            {p.label}
          </button>
        ))}
      </div>

      <label className="sr-only" htmlFor="sales-status">Status</label>
      <select id="sales-status" value={filters.status} onChange={set('status')} className={control}>
        <option value="">All statuses</option>
        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>

      <label className="sr-only" htmlFor="sales-source">Source</label>
      <select id="sales-source" value={filters.source} onChange={set('source')} className={control}>
        <option value="">All sources</option>
        <option value="catalog">Catalog</option>
        <option value="concept">Concept</option>
      </select>

      <input type="date" value={filters.from} onChange={setDate('from')} max={filters.to || undefined}
             aria-label="Ordered on or after" className={control} />
      <span className="text-xs text-gray-400">to</span>
      <input type="date" value={filters.to} onChange={setDate('to')} min={filters.from || undefined}
             aria-label="Ordered on or before" className={control} />

      <label className="sr-only" htmlFor="sales-q">Search class or buyer</label>
      <input id="sales-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search class or buyer…"
             className={`${control} min-w-[14rem]`} />

      {(narrowed || period !== DEFAULT_PERIOD) && (
        <button type="button" onClick={clear}
                className="text-xs px-3 py-1.5 rounded-full border border-orange-300 text-orange-700 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-950">
          Reset
        </button>
      )}
      {(purchasesQ.isFetching || analyticsQ.isFetching) && <span className="text-xs text-gray-400">Updating…</span>}
    </div>
  );

  // Filters sit ABOVE the sub-tabs on purpose: they narrow both views, so moving
  // between them must not silently change what is being counted.
  const subTabs = (
    <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700 mb-4">
      {[{ id: 'overview', label: 'Overview' }, { id: 'purchases', label: `Purchases (${total})` }].map((t) => (
        <button key={t.id} type="button" onClick={() => setView(t.id)} aria-current={view === t.id ? 'page' : undefined}
                className={`px-4 py-2 text-sm ${view === t.id
                  ? 'border-b-2 border-blue-500 font-semibold text-blue-600 dark:text-blue-400'
                  : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}>
          {t.label}
        </button>
      ))}
    </div>
  );

  const frame = (children, withTabs = false) => (
    <div>
      <h2 className="text-lg font-semibold mb-3">Sales</h2>
      {controls}
      {withTabs && subTabs}
      {children}
    </div>
  );

  if (purchasesQ.isLoading || analyticsQ.isLoading) {
    return frame(<div className="text-sm text-gray-500">Loading sales...</div>);
  }
  if (purchasesQ.isError || analyticsQ.isError) {
    return frame(<div className="text-sm text-red-500">Could not load sales data. Please try again.</div>);
  }

  const overview = (
    <>
      {/* Hero: the one number the screen leads with, with its comparison. */}
      <div className="mb-5">
        <div className="flex items-end gap-3 flex-wrap">
          <span className="text-4xl font-semibold tracking-tight text-blue-600 dark:text-blue-400">
            {money(analytics?.revenue_cents)}
          </span>
          {revenueDelta != null && (
            <span className={`text-sm font-medium pb-1 ${revenueDelta >= 0
              ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'}`}>
              <span aria-hidden="true">{revenueDelta >= 0 ? '▲' : '▼'}</span>{' '}
              {revenueDelta === Infinity ? 'new' : `${Math.abs(revenueDelta).toFixed(0)}%`}
              <span className="text-gray-500 font-normal"> vs previous {periodLabel?.toLowerCase() || 'period'}</span>
            </span>
          )}
        </div>
        <div className="text-xs text-gray-500 mt-1">
          Revenue collected{period === 'all' ? ', all time' : `, last ${periodLabel?.toLowerCase()}`}
          {narrowed ? ', filtered' : ''}
          {analytics?.prior == null && period === 'all' && ' · pick a period to compare against the one before it'}
        </div>
      </div>

      <KpiRow analytics={analytics} />

      {/* Two short panels stack beside the tall chart so the right column carries its
          own weight instead of one panel stretching into empty space. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-3 items-start">
        <div className="lg:col-span-2"><RevenueTrend series={analytics?.series} bucket={analytics?.period?.bucket} /></div>
        <div className="space-y-3">
          <SourceMix bySource={analytics?.by_source} />
          <StateMix byState={analytics?.by_state} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-5 items-start">
        <div className="lg:col-span-2"><TopClasses byClass={analytics?.by_class} /></div>
        <ExpiringSoon expiring={analytics?.expiring_soon} />
      </div>
    </>
  );

  const purchasesView = (
    <>
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-sm font-semibold">{narrowed ? 'Matching purchases' : 'Purchases'}</h3>
        <div className="text-xs text-gray-500" aria-live="polite">
          {total === 0 ? 'No matches' : `Showing ${firstOnPage}-${(page - 1) * PER_PAGE + rows.length} of ${total}`}
        </div>
      </div>

      <table className="w-full text-xs">
        <thead className="text-gray-500">
          <tr>
            <th scope="col" className="text-left py-1">Class</th><th scope="col" className="text-left">Source</th>
            <th scope="col" className="text-left">Buyer</th><th scope="col" className="text-right">Seats</th>
            <th scope="col" className="text-right">Total</th><th scope="col" className="text-left">Status</th>
            <th scope="col" className="text-left">Valid until</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.id} className="border-t border-gray-100 dark:border-gray-800">
              <td className="py-1">{p.class_title}</td>
              <td>{p.source}</td>
              <td>{p.buyer}</td>
              <td className="text-right tabular-nums">{p.seats}</td>
              <td className="text-right tabular-nums">{moneyExact(p.total_cents)}</td>
              <td>{p.status}</td>
              <td>{p.valid_until ? formatDate(p.valid_until) : '-'}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={7} className="py-3 text-gray-500">No purchases match these filters.</td></tr>
          )}
        </tbody>
      </table>

      {totalPages > 1 && (
        <div className="flex items-center gap-2 mt-3">
          <button type="button" onClick={() => setPage((n) => Math.max(1, n - 1))} disabled={page <= 1}
                  className="text-xs px-3 py-1.5 rounded-full border border-gray-300 dark:border-gray-600 disabled:opacity-40">
            Previous
          </button>
          <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
          <button type="button" onClick={() => setPage((n) => Math.min(totalPages, n + 1))} disabled={page >= totalPages}
                  className="text-xs px-3 py-1.5 rounded-full border border-gray-300 dark:border-gray-600 disabled:opacity-40">
            Next
          </button>
        </div>
      )}
    </>
  );

  return frame(view === 'overview' ? overview : purchasesView, true);
}
